import React, { useState } from 'react';
import { Calendar, Check, Plus, X, RefreshCw } from 'lucide-react';
import { dealsApi } from '../services/api';
import './DealTimeline.css';

const MILESTONES = [
  { key: 'ioiDate',                label: 'IOI',                   short: 'IOI'      },
  { key: 'loiDate',                label: 'LOI',                   short: 'LOI'      },
  { key: 'diligenceAgreementDate', label: 'Diligence & Agreement', short: 'DD/Agmt.' },
  { key: 'closingDate',            label: 'Closing',               short: 'Close'    },
  { key: 'postCloseDate',          label: 'Post Close Completed',  short: 'PCC'      },
];

function formatDate(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
}

function getMilestoneState(dateStr) {
  if (!dateStr) return 'empty';
  const d = new Date(dateStr + 'T00:00:00');
  return d <= new Date() ? 'done' : 'upcoming';
}

export default function DealTimeline({ deal, onUpdate }) {
  const [editing, setEditing] = useState(null); // milestone key
  const [inputVal, setInputVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingRounds, setEditingRounds] = useState(false);
  const [roundsVal, setRoundsVal] = useState(deal.negotiationRounds ?? '');

  const openEdit = (key) => {
    setEditing(key);
    setInputVal(deal[key] ? deal[key].substring(0, 10) : '');
  };

  const cancelEdit = () => { setEditing(null); setInputVal(''); };

  const saveEdit = async (key) => {
    setSaving(true);
    try {
      await onUpdate(deal.id, { [key]: inputVal || null });
    } finally {
      setSaving(false);
      setEditing(null);
      setInputVal('');
    }
  };

  // Find the index of the last completed milestone for progress line
  const lastDoneIdx = MILESTONES.reduce((acc, m, i) =>
    getMilestoneState(deal[m.key]) === 'done' ? i : acc, -1);

  return (
    <div className="dtl-container">
      <div className="dtl-header">
        <h3>Deal Timeline</h3>
        <p className="dtl-subtitle">Track key milestones throughout the deal process</p>
      </div>

      <div className="dtl-track-wrap">
        {/* Progress line background */}
        <div className="dtl-line-bg" />
        {/* Progress line filled */}
        <div
          className="dtl-line-fill"
          style={{
            width: lastDoneIdx < 0
              ? '0%'
              : `${(lastDoneIdx / (MILESTONES.length - 1)) * 100}%`
          }}
        />

        <div className="dtl-nodes">
          {MILESTONES.map((m, i) => {
            const state = getMilestoneState(deal[m.key]);
            const isEditing = editing === m.key;

            return (
              <div key={m.key} className="dtl-node-wrap">
                <button
                  className={`dtl-node dtl-node--${state}`}
                  onClick={() => openEdit(m.key)}
                  title={state === 'empty' ? 'Click to set date' : 'Click to edit'}
                >
                  {state === 'done'
                    ? <Check size={14} strokeWidth={2.5} />
                    : state === 'upcoming'
                      ? <Calendar size={13} />
                      : <Plus size={14} strokeWidth={2.5} />
                  }
                </button>
                <div className="dtl-node-label">{m.label}</div>
                <div className={`dtl-node-date dtl-node-date--${state}`}>
                  {formatDate(deal[m.key]) || <span className="dtl-not-set">Not set</span>}
                </div>

                {isEditing && (
                  <div className="dtl-popover">
                    <div className="dtl-popover-title">{m.label}</div>
                    <input
                      type="date"
                      className="dtl-date-input"
                      value={inputVal}
                      onChange={e => setInputVal(e.target.value)}
                      autoFocus
                    />
                    <div className="dtl-popover-actions">
                      {inputVal && (
                        <button
                          className="dtl-btn-clear"
                          onClick={() => setInputVal('')}
                          type="button"
                        >
                          Clear
                        </button>
                      )}
                      <button className="dtl-btn-cancel" onClick={cancelEdit} type="button">
                        <X size={13} />
                      </button>
                      <button
                        className="dtl-btn-save"
                        onClick={() => saveEdit(m.key)}
                        disabled={saving}
                        type="button"
                      >
                        <Check size={13} />
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary cards */}
      <div className="dtl-summary">
        {MILESTONES.map(m => {
          const state = getMilestoneState(deal[m.key]);
          return (
            <div key={m.key} className={`dtl-summary-card dtl-summary-card--${state}`} onClick={() => openEdit(m.key)}>
              <span className="dtl-summary-label">{m.label}</span>
              <span className="dtl-summary-date">
                {formatDate(deal[m.key]) || '—'}
              </span>
              <span className={`dtl-summary-status dtl-summary-status--${state}`}>
                {state === 'done' ? 'Completed' : state === 'upcoming' ? 'Upcoming' : 'Not set'}
              </span>
            </div>
          );
        })}
      </div>

      {/* Negotiation Rounds */}
      <div className="dtl-rounds-section">
        <div className="dtl-rounds-card">
          <RefreshCw size={16} className="dtl-rounds-icon" />
          <div className="dtl-rounds-body">
            <span className="dtl-rounds-label">Negotiation Rounds</span>
            {editingRounds ? (
              <div className="dtl-rounds-edit">
                <input
                  type="number"
                  min="0"
                  className="dtl-rounds-input"
                  value={roundsVal}
                  onChange={e => setRoundsVal(e.target.value)}
                  autoFocus
                />
                <button className="dtl-btn-cancel" onClick={() => { setEditingRounds(false); setRoundsVal(deal.negotiationRounds ?? ''); }}>
                  <X size={13} />
                </button>
                <button
                  className="dtl-btn-save"
                  disabled={saving}
                  onClick={async () => {
                    setSaving(true);
                    await onUpdate(deal.id, { negotiationRounds: roundsVal !== '' ? parseInt(roundsVal) : null });
                    setSaving(false);
                    setEditingRounds(false);
                  }}
                >
                  <Check size={13} /> Save
                </button>
              </div>
            ) : (
              <span className="dtl-rounds-value" onClick={() => setEditingRounds(true)}>
                {deal.negotiationRounds != null ? deal.negotiationRounds : <span className="dtl-not-set">Not set</span>}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
